/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { autorun, runInAction, comparer } from 'mobx';
import {
    computedFn,
    FULFILLED,
    PENDING,
    PromiseState,
    REJECTED,
} from 'mobx-utils';
import { DeepMap } from 'mobx-utils/lib/deepMap';
import {
    GenericAsyncFunction,
    GenericFunction,
    PromiseLikeUnwrap,
    PromiseType,
} from './utilityTypes';
import {
    toLazyObservablePromise,
    LazyObservablePromise,
    OnFulFilled,
    OnRejected,
    RejectFn,
    ResolveFn,
} from './LazyObservablePromise';
import {
    isFulfilledPromiseObservable,
    isPendingPromiseObservable,
} from './promises';
import { isAlive, isStateTreeNode } from 'mobx-state-tree';

export type PromiseContinuationUnwrap<T> = T extends PromiseLike<infer U>
    ? PromiseContinuationUnwrap<U>
    : // Here, the first type of ContinuationPayload is already encoded in R1
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends ContinuationPayload<any, infer R1, infer R2>
    ? PromiseContinuationUnwrap<LazyObservablePromise<R1 | R2>>
    : T;

export type ContinuationPayload<T, R1, R2> = {
    isContinuationPayload: true;
    origin: LazyObservablePromise<T>;
    onFulfilled: OnFulFilled<T, R1>;
    onRejected: OnRejected<R2>;
};

export type AsyncViewPromise<T> = LazyObservablePromise<T> & {
    continuation<R1 = T, R2 = never>(
        onFulfilled?: OnFulFilled<T, R1>,
        onRejected?: OnRejected<R2>
    ): ContinuationPayload<T, R1, R2>;
};

type AsyncView = <T extends GenericFunction>(
    fn: T,
    name?: string
) => (
    ...args: Parameters<T>
) => AsyncViewPromise<PromiseContinuationUnwrap<ReturnType<T>>>;

type AsyncAction = <T extends GenericAsyncFunction>(
    fn: T
) => (
    ...args: Parameters<T>
) => LazyObservablePromise<PromiseType<ReturnType<T>>>;

type MakeAsyncActions = <Props extends Record<string, unknown>>(actions: {
    [Property in keyof Props]: Props[Property];
}) => {
    [Property in keyof Props]: Props[Property] extends GenericAsyncFunction
        ? (
              ...args: Parameters<Props[Property]>
          ) => LazyObservablePromise<
              PromiseLikeUnwrap<ReturnType<Props[Property]>>
          >
        : never;
};

type MakeAsyncView = <Props extends Record<string, unknown>>(views: {
    [Property in keyof Props]: Props[Property];
}) => {
    [Property in keyof Props]: Props[Property] extends GenericFunction
        ? (
              ...args: Parameters<Props[Property]>
          ) => AsyncViewPromise<
              PromiseContinuationUnwrap<ReturnType<Props[Property]>>
          >
        : AsyncViewPromise<PromiseContinuationUnwrap<Props[Property]>>;
};

const asyncAction: AsyncAction =
    fn =>
    (...args) =>
        toLazyObservablePromise(fn(...args));

const runAsyncAction = <T extends GenericAsyncFunction>(
    fn: T,
    ...args: Parameters<T>
): LazyObservablePromise<PromiseType<ReturnType<T>>> =>
    asyncAction(fn)(...args);

const makeAsyncActions: MakeAsyncActions = actions => {
    const entries = [];

    for (const key in actions) {
        entries.push([key, asyncAction(actions[key] as GenericAsyncFunction)]);
    }

    return Object.fromEntries(entries);
};

function isContinuationPayload(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any
): payload is ContinuationPayload<unknown, unknown, unknown> {
    return payload?.isContinuationPayload === true;
}

function makeContinuation(
    promise: AsyncViewPromise<unknown> | AsyncViewPromise<never>
): AsyncViewPromise<unknown>['continuation'] {
    return (onFulfilled, onRejected) => {
        return {
            isContinuationPayload: true,
            origin: promise,
            onFulfilled,
            onRejected,
        };
    };
}

// Will break this later, and also add tests
// eslint-disable-next-line max-lines-per-function
const asyncView: AsyncView = fn => {
    type AsyncViewPayload = {
        promise: AsyncViewPromise<unknown> | AsyncViewPromise<never>;
        resolveFn?: ResolveFn<unknown>;
        rejectFn?: RejectFn;
    };
    const asyncViews = new DeepMap<AsyncViewPayload>();

    const getAsyncView = (args: any[]) => {
        const entry = asyncViews.entry(args);
        if (entry.exists()) return entry.get();
        return undefined;
    };

    const setAsyncView = (args: any[], payload: AsyncViewPayload) => {
        const entry = asyncViews.entry(args);
        entry.set(payload);
    };

    const checkValueSafely = (
        promise:
            | AsyncViewPromise<unknown>
            | AsyncViewPromise<never>
            | undefined,
        value: unknown
    ) =>
        runInAction(() => {
            if (promise === undefined) return false;
            if (isStateTreeNode(promise.value)) {
                if (!isAlive(promise.value)) return false;
                return promise.value === value;
            }
            return comparer.shallow(promise.value, value);
        });

    const checkStateSafely = (
        promise:
            | AsyncViewPromise<unknown>
            | AsyncViewPromise<never>
            | undefined,
        state: PromiseState
    ) => runInAction(() => promise?.state === state);

    // eslint-disable-next-line complexity
    const init = (args: any[], state?: PromiseState, value?: unknown) => {
        let asyncView = getAsyncView(args);

        if (checkStateSafely(asyncView?.promise, PENDING)) return;

        if (!state || state === PENDING || isPendingPromiseObservable(value)) {
            let resolveFn: ResolveFn<unknown>;
            let rejectFn: RejectFn;

            const promise = new LazyObservablePromise((resolve, reject) => {
                resolveFn = resolve;
                rejectFn = reject;
            }, true) as AsyncViewPromise<unknown>;

            setAsyncView(args, {
                promise,
                resolveFn: resolveFn!,
                rejectFn: rejectFn!,
            });
        } else if (state === FULFILLED) {
            setAsyncView(args, {
                promise: LazyObservablePromise.resolve(
                    value
                ) as AsyncViewPromise<unknown>,
                resolveFn: undefined,
                rejectFn: undefined,
            });
        } else {
            setAsyncView(args, {
                promise: LazyObservablePromise.reject(
                    value
                ) as AsyncViewPromise<never>,
                resolveFn: undefined,
                rejectFn: undefined,
            });
        }

        asyncView = getAsyncView(args);
        asyncView!.promise.continuation = makeContinuation(asyncView!.promise);
    };

    const memoizedFn = computedFn((...args) => {
        const value = fn(...args);

        if (isContinuationPayload(value)) return value;
        if (value instanceof LazyObservablePromise) return value;
        if (value instanceof Promise) return toLazyObservablePromise(value);
        return LazyObservablePromise.resolve(value);
    });

    const resolveSync = <T, R1, R2>(
        args: any[],
        promise: LazyObservablePromise<T>,
        onFulfilled?: OnFulFilled<T, R1>,
        onRejected?: OnRejected<R2>
    ) => {
        const asyncView = getAsyncView(args);

        if (isFulfilledPromiseObservable(promise)) {
            const result = onFulfilled
                ? onFulfilled(promise.value as T)
                : promise.value;

            if (!checkValueSafely(asyncView?.promise, result))
                init(args, FULFILLED, result);
        } else {
            if (onRejected) init(args, FULFILLED, onRejected(promise.value));
            else init(args, REJECTED, promise.value);
        }
    };

    const resolveAsync = <T, R1, R2>(
        args: any[],
        promise: LazyObservablePromise<T>,
        onFulfilled?: OnFulFilled<T, R1>,
        onRejected?: OnRejected<R2>
    ) => {
        const asyncView = getAsyncView(args);

        if (isFulfilledPromiseObservable(promise)) {
            if (onFulfilled)
                asyncView!.resolveFn!(onFulfilled(promise.value as T));
            else asyncView!.resolveFn!(promise.value);
        } else {
            if (onRejected) asyncView!.resolveFn!(onRejected(promise.value));
            else asyncView!.rejectFn!(promise.value);
        }
    };

    // eslint-disable-next-line complexity
    const runResolver = <T, R1, R2>(
        args: any[],
        promise: LazyObservablePromise<T>,
        onFulfilled?: OnFulFilled<T, R1>,
        onRejected?: OnRejected<R2>
    ) => {
        const asyncView = getAsyncView(args);

        try {
            const resolve = checkStateSafely(asyncView?.promise, PENDING)
                ? resolveAsync
                : resolveSync;
            resolve(args, promise, onFulfilled, onRejected);
        } catch (e) {
            if (!checkStateSafely(asyncView?.promise, PENDING))
                init(args, REJECTED, e);
            else asyncView!.rejectFn!(e);
        }
    };

    return (...args) => {
        const result = memoizedFn(...args);

        if (isContinuationPayload(result)) {
            if (isPendingPromiseObservable(result.origin)) init(args);
            else
                runResolver(
                    args,
                    result.origin,
                    result.onFulfilled,
                    result.onRejected
                );
        } else {
            if (isPendingPromiseObservable(result)) init(args);
            else runResolver(args, result);
        }

        return getAsyncView(args)!.promise as AsyncViewPromise<
            PromiseContinuationUnwrap<ReturnType<typeof fn>>
        >;
    };
};

const makeAsyncViews: MakeAsyncView = views => {
    type ViewsType = typeof views;

    const asyncViews = {} as {
        [Property in keyof ViewsType]: ViewsType[Property] extends GenericFunction
            ? (
                  ...args: Parameters<ViewsType[Property]>
              ) => AsyncViewPromise<
                  PromiseContinuationUnwrap<ReturnType<ViewsType[Property]>>
              >
            : AsyncViewPromise<PromiseContinuationUnwrap<ViewsType[Property]>>;
    };

    for (const key in views) {
        const descriptor = Object.getOwnPropertyDescriptor(views, key);
        if (!descriptor) continue;

        if (descriptor.get) {
            descriptor.get = asyncView(descriptor.get);
        } else {
            descriptor.value = computedFn(asyncView(descriptor.value));
        }

        Object.defineProperty(asyncViews, key, descriptor);
    }

    return asyncViews;
};

const asyncAutoRun = <T>(action: () => PromiseLike<T>): PromiseLike<T> => {
    let promise: PromiseLike<T>;

    autorun(() => {
        promise = action();
    });

    // Making a late access to `promise` so that typescript does not
    // consider I am accessing the variable prior to its assignment.
    const getter = () => promise;
    return getter();
};

function serialAsyncAction<T extends GenericAsyncFunction>(
    action: T
): (...args: Parameters<T>) => ReturnType<T> {
    let current: undefined | ReturnType<T>;

    return (...args: Parameters<T>) => {
        if (current) return current;
        current = action(...args) as ReturnType<T>;
        Promise.resolve(current).finally(() => (current = undefined));
        return current;
    };
}

export {
    asyncAutoRun,
    runAsyncAction,
    makeAsyncViews,
    asyncView,
    asyncAction,
    makeAsyncActions,
    serialAsyncAction,
};
